/**
 * English messages for UI-HONEST (BUILD-PLAN §3.16; decision 50: a control
 * that pretends to work is a defect, fixed or honestly disabled, never a
 * mockup). Spread into `en` (./en.ts) as new top-level namespaces, so these
 * keys are `MessageKey`s like any other; ./controls.ar.ts is the Arabic.
 *
 * - `pending`: why a control is shown disabled. Each sentence names what it
 *   waits for; `{{slice}}` is the build-plan or roadmap id it waits for
 *   (docs/team/BUILD-PLAN.md), passed isolated left to right by
 *   components/PendingAction.tsx, so no sentence carries Latin letters of its
 *   own.
 * - `confirmDialog`: the shared confirmation (components/ConfirmedAction.tsx).
 * - `sessionControl`: ending sessions (Profile, Security Center).
 * - `mfaSetup`: turning two-step sign-in on and off (Profile).
 * - `apiClientCreate`: a new API client and its one-time secret.
 * - `documentActions`: the labels of document-list actions on screens the
 *   document editor rebuilds (they keep their other English until then).
 * - `notificationActions`: what Mark read and Mark all read report.
 * - `notAvailable`: a whole page whose feature runs nothing yet
 *   (components/NotAvailable.tsx): the state, what the page does not do,
 *   and the tag the sidebar and the command palette put beside its name.
 */
export const controlsEn = {
  pending: {
    salesOrderEditor: "Arrives with the sales order editor ({{slice}}).",
    deliveryFromOrder:
      "Deliveries are made from a sales order, with the sales order editor ({{slice}}).",
    purchaseEditor:
      "Arrives with the purchase order and goods receipt editor ({{slice}}).",
    invoiceEditor: "Arrives with the invoice editor ({{slice}}).",
    paymentEditor: "Arrives with the payment editor ({{slice}}).",
    journalEditor: "Arrives with the journal entry editor ({{slice}}).",
    quoteEditor: "Arrives with the quote editor ({{slice}}).",
    returns: "Arrives with credit notes and returns ({{slice}}).",
    dataImport: "Arrives with data import ({{slice}}).",
    periodClose: "Arrives with the month-end close ({{slice}}).",
    statements: "Arrives with the financial statements screens ({{slice}}).",
    fixedAssets: "Arrives with the fixed asset screens ({{slice}}).",
    revaluation: "Arrives with currency revaluation ({{slice}}).",
    stockDocuments: "Arrives with stock transfers and stocktakes ({{slice}}).",
    stockMovement: "Arrives with the rebuilt stock movement ({{slice}}).",
    valuation: "Arrives with the stock valuation screens ({{slice}}).",
    traceability: "Arrives with the traceability page ({{slice}}).",
    manufacturing: "Arrives with the manufacturing screens ({{slice}}).",
    mrp: "Planning is not built yet, and whether it is in the launch is still open ({{slice}}).",
    pricing: "Arrives with price list prices ({{slice}}).",
    creditApproval:
      "Credit limits will be decided through approvals ({{slice}}).",
    people: "Arrives with the people and roles screens ({{slice}}).",
    approvalDesigner: "Arrives with the approval matrix designer ({{slice}}).",
    settingsSave:
      "Saving settings arrives with its own slice ({{slice}}). Until then they are read only.",
    dataExport: "Arrives with the customer data export ({{slice}}).",
    packInstall: "Arrives with the pack installer ({{slice}}).",
    packUpgrade: "Arrives with pack upgrades ({{slice}}).",
    marketplace: "Arrives with the marketplace screens ({{slice}}).",
    editions: "Arrives with editions ({{slice}}).",
    afterLaunch: "Planned for after the launch ({{slice}}).",
    notScheduled:
      "Not built yet: the roadmap has it ({{slice}}), but no build slice is scheduled.",
    unplanned:
      "Not built yet, and no build slice is planned for it. The gap is registered as {{slice}}.",
    taxLedger: "Arrives with the tax ledger ({{slice}}).",
    vatReturn: "Arrives with the VAT return ({{slice}}).",
    statementEngine: "Arrives with the statement engine ({{slice}}).",
  },
  pager: {
    label: "Pages",
    previous: "Previous page",
    next: "Next page",
    page: "Page {{page}}",
    more: "More rows follow on the next page.",
  },
  confirmDialog: {
    working: "Working…",
  },
  sessionControl: {
    revokeTitle: "End this session?",
    revokeBody:
      "The device {{device}} is signed out at once and must sign in again.",
    revokeConfirm: "End session",
    revoked: "The session has ended.",
    revokeAllTitle: "End every other session?",
    revokeAllBody_one:
      "{{count}} other session is signed out at once. This one stays signed in.",
    revokeAllBody_other:
      "{{count}} other sessions are signed out at once. This one stays signed in.",
    revokeAllConfirm: "End other sessions",
    revokedAll_one: "{{count}} session has ended.",
    revokedAll_other: "{{count}} sessions have ended.",
  },
  mfaSetup: {
    setupTitle: "Set up two-step sign-in",
    step1:
      "Add this setup key to your authenticator app, or open the link on the phone that has the app.",
    keyLabel: "Setup key",
    openApp: "Open in the authenticator app",
    step2:
      "Save these recovery codes where you can reach them without your phone. Each works once, and they are shown only now.",
    recoveryCodes: "Recovery codes",
    step3: "Enter the {{digits}}-digit code your app shows now.",
    codeLabel: "Code from the app",
    turnOn: "Turn on",
    starting: "Starting…",
    checking: "Checking…",
    enabled: "Two-step sign-in is on.",
    disableTitle: "Turn off two-step sign-in",
    disableBody:
      "Enter the code your authenticator app shows now, or one of your recovery codes.",
    disableCodeLabel: "Code or recovery code",
    turnOff: "Turn off",
    disabled: "Two-step sign-in is off.",
  },
  apiClientCreate: {
    title: "New API client",
    scopes: "What it may do",
    scopesHint:
      "Choose the permissions this client gets. You can give only permissions you hold.",
    filter: "Filter permissions",
    narrow:
      "Showing the first {{limit}} matches. Narrow the filter to see the rest.",
    chosen_one: "{{count}} permission chosen",
    chosen_other: "{{count}} permissions chosen",
    create: "Create client",
    creating: "Creating…",
    createdTitle: "Copy the secret now",
    createdBody:
      "This secret is shown only once. Store it in your integration's secret store: {{product}} cannot show it again.",
    secret: "Client secret",
    copy: "Copy",
    copied: "Copied",
    done: "Done",
  },
  documentActions: {
    newSalesOrder: "New sales order",
    newSalesInvoice: "New invoice",
    captureSupplierInvoice: "Capture invoice",
    newPurchaseOrder: "New purchase order",
    newGoodsReceipt: "New goods receipt",
    newPayment: "New payment",
    newJournalEntry: "New journal entry",
    newQuotation: "New quotation",
    newBom: "New BOM",
    runMrp: "Run MRP",
    newInspection: "New inspection",
    submit: "Submit",
    post: "Post",
    reverse: "Reverse",
  },
  notificationActions: {
    allRead: "Every notification is marked read.",
  },
  notAvailable: {
    title: "Not available yet",
    navTag: "Not available yet",
    noFigures: "Nothing on this page runs yet, so it shows no figures.",
    reportBuilder:
      "The report builder does not run reports yet. Reports cannot be designed, run, scheduled or exported here.",
    pivot:
      "The pivot viewer does not compute pivot tables yet. Pivot tables cannot be created or opened here.",
    dashboardBuilder:
      "The dashboard builder does not build dashboards yet. Dashboards cannot be created, filled with widgets or shared here.",
  },
};
